import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useRouterState } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMinutes, partsClosedReason, VISIT_WINDOW_DAYS } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem, tap } from '../../lib/idempotency';
import { useDraft } from '../../lib/useDraft';
import { draftKey } from '../../lib/drafts';
import { useSession } from '../session';
import { timeInTz } from '../../lib/format';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button, bigButton } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import {
  ErrorState,
  ForbiddenState,
  OfflineState,
  awaitingServer,
  isForbidden,
  mutationState,
  shownState,
  StaleNotice,
  refreshFailed,
  refreshUntilShown,
  drawnAs,
} from '../ui/QueryStates';
import { CHECKING_LABEL, useQueueRead, useUnsentWrite } from '../ui/useConnection';
import { useFailedWriteFor } from '../../lib/failed-writes';
import { UnitsAtAddressCard } from '../equipment/UnitsAtAddressCard';
import { Skeleton } from '../ui/Skeleton';
import { VISIT_STATUS, display } from '../status';
import { money, invoiceBalanceCents } from '../money';
import { VisitChecklist } from './VisitChecklist';
import { useUnsentTicks, withUnsentTicks } from './unsentTicks';
import { orderVisitCompletion } from '../../lib/offline-replay';
import { PartsUsedCard } from '../inventory/PartsUsedCard';
import { PhotoGrid } from '../photos/PhotoGrid';
import { CustomFieldsCard } from '../customfields/CustomFieldsCard';
import { IconJobs, IconChevronLeft, IconPlay, IconStop, IconTimer } from '../icons';
import { WithOrgClock } from '../ui/WithOrgClock';
import {
  UnderlineTabs,
  UnderlineTabList,
  UnderlineTab,
  UnderlineTabPanel,
} from '../ui/Tabs';
import { PlannedVsActual } from './PlannedVsActual';
import { FlagProblem } from './FlagProblemSheet';
import { JobTicketsCard, linkedTicketOf } from './JobTicketsCard';
import { JobPartsList } from './JobPartsList';

/**
 * The screen a tech works from at the door (PRD v2 §5). One primary action at a time,
 * driven by the visit state machine — the button says the next real thing, so nobody has
 * to know what "en route" means as a state.
 *
 * Everything above the fold is what you need standing on a porch: who, where, how to
 * reach them, how to get in.
 */
export function TechVisitPage() {
  return (
    <WithOrgClock what="this stop" icon={IconJobs} title="Stop">
      {(tz) => <TechVisit tz={tz} />}
    </WithOrgClock>
  );
}

function TechVisit({ tz }: { tz: string }) {
  const { visitId } = useParams({ from: '/tech/visits/$visitId' });
  const trpc = useTRPC();
  const qc = useQueryClient();
  const q = useQuery(trpc.visit.get.queryOptions({ visitId }));
  const running = useQuery(trpc.costing.myRunningTimer.queryOptions());
  // Persisted to IndexedDB per visit, not held in React state. A tech types up what they
  // found standing in a crawlspace; iOS reclaims the tab, or they tap Directions and come
  // back, and the note was gone. `useDraft` was written for exactly this in M0 and had
  // zero importers until now. Keyed by the writer too (MAP 225a): on a shared tablet the
  // next tech to open this stop must not be shown what the last one typed.
  const { userId } = useSession();
  const [note, setNote, clearNote] = useDraft(
    { kind: 'visit-note', userId, id: visitId },
    '',
  );
  const [, tick] = useState(0);
  const [collecting, setCollecting] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'cash' | 'check' | 'other'>('check');
  const [reference, setReference] = useState('');
  const [tip, setTip] = useState('');
  /**
   * Which half of the work order is showing (119, ref 23411385: Information | Execution &
   * Report).
   *
   * ⭐ **The default FOLLOWS THE SHIFT, and that is why this is not `useUrlTab`.** Before the
   * work starts a tech is reading what they are walking into; the moment they tap Start the
   * screen they want is the checklist. A fixed default costs one tap per job on a surface
   * whose whole design is "one tap per stop".
   *
   * ⚠️ The choice is stamped with the status it was made AT, and dropped when the status
   * moves — the `usePendingOverride` idea (an override is a bet against a watermark), with
   * the visit's status as the watermark. Holding a bare `tab` string instead would pin the
   * screen to Information across the Start the tech just tapped; mirroring status into state
   * with an effect would be the stale-copy bug that file's docstring exists to warn about.
   */
  const [picked, setPicked] = useState<{ tab: string; atStatus: string } | null>(null);
  /**
   * The ticket the address names (`#ticket-<id>`: the bell's link for a tech, ops insights 5a).
   * Its card is on Execution & Report, so arriving by the link shows that half, ONCE per link:
   * the tech may go back to Information and stay there. `JobTicketsCard` does the landing.
   */
  const linkedTicketId = linkedTicketOf(
    useRouterState({ select: (s) => s.location.hash }),
  );
  const [tabShownFor, setTabShownFor] = useState<string | undefined>(undefined);

  // A clock that never re-renders shows "just started" three hours into the job, which is
  // worse than showing nothing. Re-render every 30s while it runs, and only while it runs.
  // This MUST sit above the early returns below — a hook after a conditional return
  // changes the hook count between renders and React tears the component down.
  const timerRunning = Boolean(running.data);
  /**
   * ⚠️ TWO clocks can be live on this screen, and 119 added the second one.
   *
   * `timerRunning` is the tech's TIME ENTRY (costing). The planned-vs-actual card measures
   * the VISIT — `actual_start_at` with no `actual_end_at` — and those are different facts: a
   * tech can be on site with the costing clock off. Ticking on the first alone would have
   * frozen the new card at whatever minute the page mounted, which is precisely the "shows
   * just started three hours in" failure the interval exists to prevent.
   */
  const visitRunning = q.data?.visit?.status === 'in_progress';
  const anyClockRunning = timerRunning || visitRunning;
  useEffect(() => {
    if (!anyClockRunning) return;
    const id = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [anyClockRunning]);

  // Ticks written on this phone that the server has not been told about yet. A HOOK, so it
  // belongs up here with the others and above the early returns — see the note on the timer
  // interval above. It is folded over the server's checklists further down, once `q.data`
  // exists; `withUnsentTicks` is pure and may live there, this may not.
  const unsentTicks = useUnsentTicks(visitId);

  /*
   * 🔴 Read from the QUEUE, not from `useMutation()`'s own state, and above the early
   * returns because they are hooks. An observer is rebuilt on every mount, so a guard read
   * from one says `idle` the instant the tech navigates away and back — with the write still
   * queued in the cache and the balance still showing, because the server has not heard.
   * That is the same defect this ticket is about, one navigation later.
   */
  const invoiceId = q.data?.invoice?.id;
  const jobId = q.data?.job.id;
  /**
   * 🔴 **Two questions, not one — and the second is what stops a double payment.**
   *
   * `useUnsentWrite` releases the control the moment its write leaves `queued`/`sending`, and
   * `error` is one of the states it leaves TO. So a payment that reached the server and lost
   * only its response ends up logged in the refused-write log *and* with this button live
   * again — and the next tap calls `idem()`, minting a FRESH key the server's dedupe cannot
   * connect to the first. That is 016's defect one state along, and `UnsentWorkPanel` is what
   * walks the tech back to this button to commit it.
   *
   * A refused payment is retried from the panel, which replays the ORIGINAL key. That is the
   * only safe way to send it again, so this control stays shut until it is dealt with.
   */
  const paymentRefused = useFailedWriteFor(
    'payment.recordManual',
    invoiceId,
    'invoiceId',
  );
  const paymentUnsent =
    useUnsentWrite('payment.recordManual', invoiceId, 'invoiceId') || paymentRefused;
  const noteUnsent = useUnsentWrite('note.append', jobId, 'entityId');
  // Every hook is called on every render, THEN combined: `useUnsentWrite(a) || useUnsentWrite(b)`
  // skips the later hooks once the first is true, and React throws on the changed hook count,
  // which crashed this screen on the first tap of On my way or Start work.
  const enRouteUnsent = useUnsentWrite('visit.enRoute', visitId, 'visitId');
  const startUnsent = useUnsentWrite('visit.start', visitId, 'visitId');
  const abortUnsent = useUnsentWrite('visit.abort', visitId, 'visitId');
  const completeUnsent = useUnsentWrite('visit.complete', visitId, 'visitId');
  const visitStateUnsent = enRouteUnsent || startUnsent || abortUnsent || completeUnsent;
  // Until this device's saved changes have been read, every control above is locked (it may
  // have a write waiting), and says why in the slot that says "Saved — sends when you're back".
  const checking = !useQueueRead();

  const refresh = () => void qc.invalidateQueries();
  const enRoute = useMutation(trpc.visit.enRoute.mutationOptions({ onSuccess: refresh }));
  // Both settle once the stop is read again, so neither is live over the stop it just moved.
  const abort = useMutation(
    trpc.visit.abort.mutationOptions({
      onSuccess: () => refreshUntilShown(qc, trpc.visit.get.pathFilter()),
    }),
  );
  const start = useMutation(
    trpc.visit.start.mutationOptions({
      onSuccess: () => refreshUntilShown(qc, trpc.visit.get.pathFilter()),
    }),
  );
  /**
   * 🔴 The stop as drawn (`drawnAs`) when Start or "I had to turn back" was tapped: ONE state
   * change per tap, however fast the second tap comes (`changeState`).
   *
   * `disabled` cannot stop a double tap. Both taps land in the same render's handler, before
   * the pending write has re-rendered the button (a mutation's state reaches React a macrotask
   * later), and each would mint its own key, which the server cannot dedupe: the second start
   * is refused once the first lands, and offline it would wait in the queue to fail. The
   * location prompt used to absorb that second tap; since Start just starts (ADR 0001), this
   * does. One lock for both buttons: an en-route stop offers the two side by side, and a tap
   * on each in one render would send both, the second refused once the first lands.
   *
   * Released only when the Start is REFUSED, or its key can't be minted. One that lands
   * changes the row, so the lock stops matching once the stop is read again; if that read
   * never comes, a tap on the stale stop is ignored, because the server already moved it.
   * Keyed to the row's id and version, like `picked` above is to the status: this screen is
   * not remounted when the router moves it to another stop, which a status key would match.
   */
  const stateTappedAt = useRef<string | null>(null);
  /**
   * The completion is the one write on this screen that must not overtake its own siblings:
   * `assertRequiredItemsDone` refuses a stop whose required steps the server has not been
   * told about yet, and offline it has been told about none of them. `orderVisitCompletion`
   * holds the send until this visit's queued writes have landed.
   *
   * Wrapping the FUNCTION, not the resume: a paused mutation resumes itself on the
   * connectivity event — nothing on the resume path gets a say in what goes out.
   */
  const completeOptions = trpc.visit.complete.mutationOptions({ onSuccess: refresh });
  const complete = useMutation({
    ...completeOptions,
    mutationFn: orderVisitCompletion(qc, completeOptions.mutationFn!),
  });
  const startTimer = useMutation(
    trpc.costing.startTimer.mutationOptions({ onSuccess: refresh }),
  );
  const stopTimer = useMutation(
    trpc.costing.stopTimer.mutationOptions({ onSuccess: refresh }),
  );
  const addNote = useMutation(
    trpc.note.append.mutationOptions({
      // The draft this note is typed in: kept on its queued row, so whichever way the note
      // lands (this screen, or the queue after a remount or a restart), exactly this draft
      // is cleared (offline-replay's onLanded).
      meta: { draftKey: draftKey('visit-note', userId, visitId) },
      onSuccess: () => {
        clearNote();
        refresh();
      },
    }),
  );
  const takePayment = useMutation(
    trpc.payment.recordManual.mutationOptions({
      onSuccess: () => {
        setCollecting(false);
        setAmount('');
        setReference('');
        setTip('');
        refresh();
      },
    }),
  );

  // Four states, in the only order that is honest — see queryState. A paused query is
  // ALSO pending, so checking pending first is what rendered a skeleton that could never
  // resolve for a tech with no signal and nothing cached.
  //
  // `shownState` rather than `queryState`: a stop the tech is standing at is not thrown away
  // because a poll failed (042).
  const state = shownState(q, q.data);
  if (state === 'offline') {
    return (
      <div className="p-4">
        <OfflineState icon={IconJobs} what="this stop" />
      </div>
    );
  }
  if (state === 'loading') {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-28" />
        <Skeleton className="h-14" />
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="p-4">
        {/* `visit.get` is gated on `visit:read` now — see TechTodayPage for why a role that
            lacks it can still reach this URL. Retrying a refusal is a button that can only
            fail, and it reads as "the app is broken" rather than "this isn't your account". */}
        {isForbidden(q.error) ? (
          <ForbiddenState icon={IconJobs} what="this stop" />
        ) : (
          <ErrorState icon={IconJobs} what="this stop" onRetry={() => void q.refetch()} />
        )}
      </div>
    );
  }
  // The server answers `null` for every stop outside the tech's read window, and says no more
  // on purpose ("gone" and "someone else's" look the same), so the page names each reason.
  if (!q.data) {
    return (
      <div className="p-4">
        <EmptyState
          icon={IconJobs}
          title="Not one of your stops"
          description={`This visit isn't assigned to you, it was canceled or moved, or the ${VISIT_WINDOW_DAYS} days after its check-out are over.`}
        />
      </div>
    );
  }

  const { visit, job, customer, property, invoice, checklists } = q.data;

  /**
   * ⭐ Before the work starts, the tech is reading; once it starts, the checklist IS the job.
   * `completed`/`canceled` land on the record too — the finished stop's answer is what was
   * done, not what was planned.
   */
  const defaultTab =
    visit.status === 'scheduled' || visit.status === 'en_route' ? 'info' : 'work';
  if (linkedTicketId !== tabShownFor) {
    setTabShownFor(linkedTicketId);
    if (linkedTicketId !== undefined) setPicked({ tab: 'work', atStatus: visit.status });
  }
  // The stamp is dropped the moment the status moves, so tapping Start re-asserts the
  // derived tab rather than stranding the reader on the half they were last looking at.
  const tab = picked && picked.atStatus === visit.status ? picked.tab : defaultTab;
  const setTab = (next: string) => setPicked({ tab: next, atStatus: visit.status });
  const s = display(VISIT_STATUS, visit.status);
  const address =
    [property.address1, property.city, property.state].filter(Boolean).join(', ') || null;
  const mapsHref = address
    ? `https://maps.google.com/?q=${encodeURIComponent(address)}`
    : null;

  const runningHere =
    running.data && running.data.entry.visitId === visitId ? running.data.entry : null;
  /**
   * A clock running somewhere else — which the server refuses to let you double.
   *
   * `startTimer` takes ONE timer per user, org-wide: the partial unique index makes it a
   * database invariant and the service turns it into a sentence ("You already have a timer
   * running. Stop that one before starting another."). This screen said "Clock is off" and
   * offered Start anyway, so a tech who forgot to stop at the last stop taps a live button
   * and gets a refusal — the shape §7 calls copy naming a state the system is not in.
   *
   * ⚠️ Keyed on `visitId`, matching `runningHere` above and NOT `JobMoneyPanel`'s `jobId`.
   * That is deliberate: the server's rule is per USER, so a timer on another visit of this
   * same job is still one it will refuse. A timer started from the office panel carries
   * `visitId: null` and correctly reads as elsewhere here.
   */
  const runningElsewhere =
    running.data && running.data.entry.visitId !== visitId ? running.data : null;
  /**
   * 🔴🔴 THE FIX HAD THE HOLE IT WAS FIXING, and two reviewers found it independently.
   *
   * `runningElsewhere` reads `running.data`. Until `myRunningTimer` answers — every cold
   * open of a visit — and forever after it fails, `data` is undefined, so the guard is null
   * and the screen went back to saying "Clock is off" with Start live, for a tech who does
   * have a clock running. That is this ticket's own headline defect, reintroduced inside its
   * own remedy.
   *
   * So the CLAIM is gated on knowing: until the query answers, the screen says it is
   * checking and Start waits. A second of "Checking…" beats a confident lie followed by a
   * refusal at the door.
   *
   * 🔴🔴 …AND THEN THAT FIX HAD ITS OWN HOLE, found by the Codex adversary. Written as
   * `queryState(running) === 'ready'`, ANY failure of `myRunningTimer` — including a
   * background refetch that failed while an answer was already in hand — made this false
   * FOREVER. The screen then read "Checking your clock…" with Start disabled, no error and
   * no retry: one failed request and a tech cannot clock in for the rest of the session, on
   * the screen where the whole product is being used. "Wait until we know" had quietly
   * become "wait forever".
   *
   * Two changes, and they are different states, not one:
   *
   *  - `shownState`, so an answer already in hand still counts as knowing (042);
   *  - `clockUnknown` below, so a query that has NEVER answered says so and offers a way
   *    out instead of parking the tech behind a dead control.
   */
  const clockState = shownState(running, running.data);
  const clockKnown = clockState === 'ready' && running.data !== undefined;
  /**
   * We asked, and we got nothing back. The tech is not told to wait — they are told what
   * happened and handed the two ways forward.
   *
   * ⚠️ Start stays LIVE in this state, deliberately. The one-running-timer rule is a
   * database invariant (`time_entries_one_running_per_user_uq`) and `startTimer` turns it
   * into a sentence — "You already have a timer running. Stop that one before starting
   * another." So the server is the real guard, and blocking the button here buys nothing
   * over an honest refusal while costing a tech their clock. The lie 018 removed was the
   * confident "Clock is off"; saying "we couldn't check" is not that lie.
   */
  const clockUnknown = clockState === 'error' && running.data === undefined;
  const runningMinutes = runningHere
    ? Math.floor((Date.now() - new Date(runningHere.startedAt).getTime()) / 60000)
    : 0;

  const balanceCents = invoice ? invoiceBalanceCents(invoice) : 0;
  const tipCents = Math.max(0, Math.round(Number(tip) * 100) || 0);

  /**
   * "Waiting on the network" and "queued until there is a network" are different things,
   * and only the first one should hold the screen.
   *
   * `isPending` is true for BOTH — a paused mutation never settles — so disabling on
   * `isPending` alone meant one tap with no signal locked every button on the page for as
   * long as the tech stayed in the basement.
   *
   * The rule lives in `mutationState` rather than in a helper private to this file, so the
   * next screen that needs it inherits this answer instead of writing a slightly different
   * one — which is how it stayed a one-page rule for as long as it did.
   */
  const enRouteState = mutationState(enRoute);
  const abortState = mutationState(abort);
  const startState = mutationState(start);
  const completeState = mutationState(complete);
  const paymentState = mutationState(takePayment);
  const noteState = mutationState(addNote);

  const busy = [
    enRouteState,
    abortState,
    startState,
    completeState,
    paymentState,
  ].includes('sending');
  /**
   * A state change is already asked for — a second one would just fight it.
   *
   * The observer states are kept as the fast path for the tap that is happening right now;
   * `visitStateUnsent` is what survives a remount. Either alone is a hole.
   */
  const queuedHere =
    visitStateUnsent ||
    [enRouteState, abortState, startState, completeState].includes('queued');
  const error =
    abort.error ??
    enRoute.error ??
    start.error ??
    complete.error ??
    takePayment.error ??
    startTimer.error;

  /**
   * Required checklist steps are the one thing that can refuse a finish. The button says so
   * itself rather than failing on tap — the server is still the real gate.
   *
   * Counted over the checklists AS THE PHONE HAS THEM, ticks still in the queue included.
   * `c.requiredOpen` straight off the wire is the server's answer, and with no signal the
   * server has not heard about this morning's work — which left the whole stop unfinishable
   * indoors. `derived` is what everything below reads, including `VisitChecklist`, so the
   * button and the per-group warning cannot end up quoting different numbers.
   */
  const derived = withUnsentTicks(checklists, unsentTicks);
  const requiredOpen = derived.reduce((n, c) => n + c.requiredOpen, 0);

  /**
   * What a button says while its write is queued. The tech's question is "did that
   * count?", and the honest answer is yes — it is on the phone and it will send itself.
   */
  const queuedLabel = 'Saved — sends when you’re back';

  /**
   * ⭐ Start just starts (ADR 0001, no device location). Both Start buttons send `visit.start`
   * on the tap, with no `arrival` and no prompt before it: the one-coordinate capture and its
   * consent panel were switched off together, so nothing on this screen asks where the phone
   * is or says anything was recorded.
   */
  const startWork = () =>
    changeState((released) => start.mutate({ visitId, ...idem() }, released));
  const turnBack = () =>
    changeState((released) => abort.mutate({ visitId, ...idem() }, released));

  /**
   * One tap's state change, under `stateTappedAt`. The lock is taken before `send` mints the
   * key, so a second tap never gets one; `send` hands `released` to its write.
   */
  function changeState(send: (released: { onError: () => void }) => void) {
    const at = drawnAs(visit);
    if (stateTappedAt.current === at) return; // a second tap, on the stop as already tapped
    stateTappedAt.current = at;
    try {
      send({ onError: () => void (stateTappedAt.current = null) });
    } catch (err) {
      // Minting the key can throw before any mutation exists (storage or crypto refused), and
      // then nothing would ever settle: release the lock so the button is not dead.
      stateTappedAt.current = null;
      throw err;
    }
  }

  /** The next real thing to do, from the machine — never a menu of states. */
  const primary =
    visit.status === 'scheduled' ? (
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="ghost"
          className={bigButton}
          disabled={busy || queuedHere}
          onClick={() => enRoute.mutate({ visitId, ...idem() })}
        >
          {enRouteState === 'queued'
            ? queuedLabel
            : checking
              ? CHECKING_LABEL
              : 'On my way'}
        </Button>
        <Button className={bigButton} disabled={busy || queuedHere} onClick={startWork}>
          {startState === 'queued'
            ? queuedLabel
            : checking
              ? CHECKING_LABEL
              : 'Start work'}
        </Button>
      </div>
    ) : visit.status === 'en_route' ? (
      <div className="space-y-2">
        <Button className={bigButton} disabled={busy || queuedHere} onClick={startWork}>
          {startState === 'queued'
            ? queuedLabel
            : checking
              ? CHECKING_LABEL
              : "I'm here — start work"}
        </Button>
        {/* Turning back is a real thing that happens — wrong part on the truck, road
            closed, customer not home. It puts the visit back on the board rather than
            cancelling it, so the office can re-slot it instead of re-creating it. */}
        <Button
          variant="ghost"
          className="w-full"
          disabled={busy || queuedHere}
          onClick={turnBack}
        >
          {abortState === 'queued'
            ? queuedLabel
            : abortState === 'sending'
              ? 'Turning back…'
              : checking
                ? CHECKING_LABEL
                : 'I had to turn back'}
        </Button>
      </div>
    ) : visit.status === 'in_progress' ? (
      <Button
        className={bigButton}
        disabled={busy || queuedHere || requiredOpen > 0}
        onClick={() => complete.mutate({ visitId, ...idem() })}
      >
        {/* 'queued' before 'sending' is `mutationState`'s job now, not this file's —
            it used to be an easy thing to get backwards here, and getting it backwards
            left the button saying "Finishing…" forever with no signal. */}
        {completeState === 'queued'
          ? 'Finished — sends when you’re back'
          : completeState === 'sending'
            ? 'Finishing…'
            : checking
              ? CHECKING_LABEL
              : requiredOpen > 0
                ? `${requiredOpen} required step${requiredOpen === 1 ? '' : 's'} left`
                : 'Finish this job'}
      </Button>
    ) : null;

  return (
    <div className="space-y-4 p-4 pb-24">
      {/* Both queries: the stop itself, and the clock that decides whether Start is live. */}
      <StaleNotice
        show={refreshFailed(q, q.data) || refreshFailed(running, running.data)}
        onRetry={() => {
          void q.refetch();
          void running.refetch();
        }}
      />
      <Link
        to="/tech"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-text"
      >
        <IconChevronLeft size={15} />
        My day
      </Link>

      <div>
        <div className="flex items-start justify-between gap-2">
          <h1 className="font-heading text-xl font-bold leading-tight">
            {customer.displayName}
          </h1>
          <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
        </div>
        <p className="mt-1 text-sm">{job.title}</p>
        <p className="text-xs text-muted-foreground">
          Job #{job.jobNumber} ·{' '}
          {visit.scheduledStartAt ? timeInTz(visit.scheduledStartAt, tz) : 'No time set'}
        </p>
      </div>

      {error ? <Notice tone="danger">{error.message}</Notice> : null}

      {/* 119, ref 23411385: the two blocks the reference pins at the top of a work order.
          ABOVE the tabs on purpose — "am I over?" is the question a tech asks while doing
          the work, so it must not be one tab away from the checklist. */}
      <PlannedVsActual times={visit} tz={tz} />

      {/* Standing on a porch, these two are the whole screen. */}
      <div className="grid grid-cols-2 gap-2">
        <a
          href={customer.phone ? `tel:${customer.phone}` : undefined}
          aria-disabled={!customer.phone}
          className={`${bigButton} border border-border bg-surface ${
            customer.phone ? '' : 'pointer-events-none opacity-50'
          }`}
        >
          Call
        </a>
        <a
          href={mapsHref ?? undefined}
          target="_blank"
          rel="noreferrer"
          aria-disabled={!mapsHref}
          className={`${bigButton} border border-border bg-surface ${
            mapsHref ? '' : 'pointer-events-none opacity-50'
          }`}
        >
          Directions
        </a>
      </div>

      {/*
        ⚠️ The primary action sits ABOVE the tabs while the stop has not started, and stays
        INSIDE Execution once it has. That is not an inconsistency — it is the existing
        thumb-reach rule kept intact across the split: before Start, the tech is reading
        Information and must not switch tabs to begin; after Start, ticking the last box has
        to leave Finish directly under the thumb, which means it follows the checklist.
      */}
      {visit.status === 'scheduled' || visit.status === 'en_route' ? primary : null}

      <UnderlineTabs value={tab} onValueChange={setTab}>
        <UnderlineTabList>
          <UnderlineTab value="info">Information</UnderlineTab>
          <UnderlineTab value="work">Execution &amp; Report</UnderlineTab>
        </UnderlineTabList>

        {/*
          🔴 `keepMounted` on BOTH panels, and it is a DATA-SAFETY switch here rather than a
          perf knob (105's own ruling). This page holds four separate unsaved drafts — the
          payment amount, the method, the check number, the tip — plus a note field. Radix
          unmounts an inactive panel, so a plain tab split would throw away a half-typed
          payment the moment a tech tapped Information to re-read a gate code, with a
          customer standing there. The flat page this replaces could never lose them.
        */}
        <UnderlineTabPanel value="info" keepMounted className="space-y-4">
          <section className="rounded-[var(--radius)] border border-border bg-surface p-3">
            <p className="text-sm font-medium">{address ?? 'No address on file'}</p>
            {property.gateCode ? (
              <p className="mt-1 text-sm">
                <span className="text-muted-foreground">Gate code </span>
                <span className="font-mono font-semibold">{property.gateCode}</span>
              </p>
            ) : null}
            {property.accessNotes ? (
              <p className="mt-1 text-sm text-muted-foreground">{property.accessNotes}</p>
            ) : null}
          </section>

          {/* What to load on the van before leaving (Phase 6a, ticket 04): on Information,
              where a tech is before the stop starts, right under where the stop is. Read-only.
              A finished or canceled stop has none to load: the server answers "not found" for
              it, so it is not asked. */}
          {visit.status === 'completed' || visit.status === 'canceled' ? null : (
            <JobPartsList visitId={visitId} />
          )}

          <UnitsAtAddressCard propertyId={property.id} linkTo="none" />

          {/* The shop's own questions that the office marked field-relevant — right under
          "what you're walking into", because that's what they are. */}
          <CustomFieldsCard
            entityType="job"
            entityId={job.id}
            title="Details for this job"
            techOnly
          />

          {job.description || visit.instructions ? (
            <section className="rounded-[var(--radius)] border border-border bg-surface p-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                What you're walking into
              </p>
              {job.description ? (
                <p className="whitespace-pre-wrap text-sm">{job.description}</p>
              ) : null}
              {visit.instructions ? (
                <p className="mt-2 whitespace-pre-wrap text-sm">{visit.instructions}</p>
              ) : null}
            </section>
          ) : null}
        </UnderlineTabPanel>

        <UnderlineTabPanel value="work" keepMounted className="space-y-4">
          {/*
        Order follows the shift, not the data model. Once the work has started, the list IS
        the job and finishing comes after it — so ticking the last box leaves the finish
        button right under the thumb. Before that, the start button stays on top where a
        tech standing on a porch expects it, with the list below as a preview of the stop.
      */}
          {visit.status === 'in_progress' ? (
            <>
              <VisitChecklist checklists={derived} frozen={false} visitId={visitId} />
              {/* Parts go on while the work is happening, so on this screen they sit with the
              checklist — above the finish button, which stays last. */}
              <PartsUsedCard
                jobId={job.id}
                visitId={visitId}
                showCost={false}
                lockToMyVan
                closedReason={partsClosedReason(job)}
              />
              {/* Attached to the VISIT, not the job: a photo belongs to the trip it was taken
              on, and the office's job view gathers every visit's photos anyway. */}
              <PhotoGrid entityType="visit" entityId={visitId} compact />
              {primary}
            </>
          ) : (
            <>
              {/* ⚠️ `primary` is NOT repeated here. For `scheduled`/`en_route` it is already
              rendered above the tab strip, where a tech reading Information can reach it
              without switching tabs; for `completed`/`canceled` it is null. Leaving the old
              `{primary}` in place would have drawn two Start buttons on the same screen. */}
              <VisitChecklist
                checklists={derived}
                frozen={visit.status === 'completed' || visit.status === 'canceled'}
                visitId={visitId}
              />
              <PartsUsedCard
                jobId={job.id}
                visitId={visitId}
                showCost={false}
                lockToMyVan
                closedReason={partsClosedReason(job)}
              />
              <PhotoGrid entityType="visit" entityId={visitId} compact />
            </>
          )}

          {/* The clock. Only worth showing once there's work to be on. */}
          {visit.status === 'in_progress' || runningHere || runningElsewhere ? (
            <section className="rounded-[var(--radius)] border border-border bg-surface p-3">
              <div className="flex items-center gap-2">
                <IconTimer size={17} className="text-muted-foreground" />
                <span className="flex-1 text-sm">
                  {runningHere ? (
                    <>
                      Running ·{' '}
                      <strong>
                        {/* formatMinutes returns "—" under a minute; a live clock must never
                        look like missing data. */}
                        {runningMinutes < 1
                          ? 'just started'
                          : formatMinutes(runningMinutes)}
                      </strong>
                    </>
                  ) : runningElsewhere ? (
                    // The office panel puts this in a `title` attribute, which a tech on a
                    // phone can never see — a tooltip is not an explanation on a touch screen.
                    <>
                      Your clock is running on{' '}
                      <strong>#{runningElsewhere.job.jobNumber}</strong>
                    </>
                  ) : clockUnknown ? (
                    <>
                      Couldn&apos;t check your clock.{' '}
                      <button
                        type="button"
                        className="underline underline-offset-2"
                        onClick={() => void running.refetch()}
                      >
                        Try again
                      </button>
                    </>
                  ) : !clockKnown ? (
                    'Checking your clock…'
                  ) : (
                    'Clock is off'
                  )}
                </span>
                {runningHere ? (
                  <Button
                    variant="ghost"
                    onClick={() =>
                      stopTimer.mutate({ timeEntryId: runningHere.id, ...tap() })
                    }
                    // Behaviour-identical (`isPending` is true while queued too), but said in
                    // the vocabulary the rest of the page uses. 010 left these reads here on
                    // purpose; this is the ticket that was meant to take them.
                    disabled={awaitingServer(stopTimer)}
                  >
                    <IconStop size={15} />
                    Stop
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() =>
                      startTimer.mutate({ jobId: job.id, visitId, ...tap() })
                    }
                    // `clockUnknown` is deliberately NOT in here — see its docblock. Waiting
                    // for an answer disables Start; never getting one must not.
                    disabled={
                      awaitingServer(startTimer) ||
                      Boolean(runningElsewhere) ||
                      (!clockKnown && !clockUnknown)
                    }
                  >
                    <IconPlay size={15} />
                    Start
                  </Button>
                )}
              </div>
            </section>
          ) : null}

          {/* Money, but only what this stop owes — never a ledger. */}
          {invoice && balanceCents > 0 ? (
            <section className="rounded-[var(--radius)] border border-border bg-surface p-3">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">
                  {invoice.invoiceNumber
                    ? `Invoice #${invoice.invoiceNumber}`
                    : 'Draft invoice'}
                </span>
                <span className="font-heading text-lg font-bold tabular-nums">
                  {money(balanceCents)}
                </span>
              </div>
              {invoice.status === 'draft' ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  The office hasn't issued this yet, so there's nothing to collect.
                </p>
              ) : collecting ? (
                <div className="mt-2 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className="h-11 w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Amount"
                      inputMode="decimal"
                      aria-label="Amount collected"
                      autoFocus
                    />
                    <select
                      className="h-11 w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
                      value={method}
                      aria-label="Payment method"
                      onChange={(e) =>
                        setMethod(e.target.value as 'cash' | 'check' | 'other')
                      }
                    >
                      <option value="check">Check</option>
                      <option value="cash">Cash</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  {method === 'check' ? (
                    <input
                      className="h-11 w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="Check number"
                      aria-label="Check number"
                    />
                  ) : null}
                  {/* A tip is money ON TOP of the bill, so it gets its own field rather than
                  being buried in the amount — and it stays out of the invoice entirely. */}
                  <input
                    className="h-11 w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
                    value={tip}
                    onChange={(e) => setTip(e.target.value)}
                    placeholder="Tip on top (optional)"
                    inputMode="decimal"
                    aria-label="Tip amount"
                  />
                  {tipCents > 0 ? (
                    <p className="text-xs text-muted-foreground">
                      They hand over {money(Math.round(Number(amount) * 100) + tipCents)}{' '}
                      — {money(Math.round(Number(amount) * 100))} on the invoice and{' '}
                      {money(tipCents)} to you.
                    </p>
                  ) : null}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="ghost"
                      className={bigButton}
                      onClick={() => setCollecting(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className={bigButton}
                      /*
                       * 🔴 `busy` alone is `sending`-only, so with no signal this stayed LIVE
                       * with a payment already queued behind it — and `onSuccess` never fires
                       * offline, so the form stayed open with the amount still in it. Every
                       * further tap called `idem()` again, minting a FRESH mutationId, which is
                       * the one thing the idempotency key cannot dedupe. N taps, N payments,
                       * against a customer standing there watching.
                       */
                      disabled={
                        busy ||
                        awaitingServer(takePayment) ||
                        paymentUnsent ||
                        !(Math.round(Number(amount) * 100) > 0)
                      }
                      onClick={() =>
                        takePayment.mutate({
                          invoiceId: invoice.id,
                          amountCents: Math.round(Number(amount) * 100),
                          method,
                          reference: reference.trim() || undefined,
                          tipCents: tipCents > 0 ? tipCents : undefined,
                          ...idem(),
                        })
                      }
                    >
                      {/* `isPending` is TRUE while paused, so this read "Saving…" for as long
                      as the tech stayed in the basement — the Wave 0.2 lie, still here. */}
                      {paymentState === 'queued'
                        ? queuedLabel
                        : paymentState === 'sending'
                          ? 'Saving…'
                          : checking
                            ? CHECKING_LABEL
                            : 'Record it'}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  className={`${bigButton} mt-2`}
                  onClick={() => {
                    setAmount((balanceCents / 100).toFixed(2));
                    setTip('');
                    setCollecting(true);
                  }}
                >
                  Take payment
                </Button>
              )}
            </section>
          ) : null}

          {/* A note from the field is the record of what actually happened. */}
          <section className="rounded-[var(--radius)] border border-border bg-surface p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Leave a note
            </p>
            <textarea
              className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What you found, what you did, what needs a follow-up…"
              aria-label="Note about this job"
            />
            <Button
              className="mt-2 w-full"
              disabled={awaitingServer(addNote) || noteUnsent || note.trim().length === 0}
              onClick={() =>
                addNote.mutate({
                  entityType: 'job',
                  entityId: job.id,
                  body: note.trim(),
                  ...idem(),
                })
              }
            >
              {noteState === 'queued'
                ? 'Saved — sends when you’re back'
                : noteState === 'sending'
                  ? 'Saving…'
                  : checking
                    ? CHECKING_LABEL
                    : 'Save note'}
            </Button>
          </section>

          {/* Beside the note: a problem the office must act on, not just a record (ops
              insights Phase 5a, ticket 03). Offline like the note, photos and all. */}
          <FlagProblem visitId={visitId} userId={userId} />
          <JobTicketsCard
            jobId={job.id}
            userId={userId}
            tz={tz}
            linkedTicketId={linkedTicketId}
          />

          {visit.status === 'completed' ? (
            <Notice tone="success">
              Finished{visit.actualEndAt ? ` at ${timeInTz(visit.actualEndAt, tz)}` : ''}.
              Head back to your day for the next stop.
            </Notice>
          ) : null}
        </UnderlineTabPanel>
      </UnderlineTabs>
    </div>
  );
}
